import { BLOCKS_VIEW_ROUTING_MARGIN, BLOCKS_VIEW_ROUTING_SEP } from '../consts';
import type { Vector } from '@ardoq/graph';
import { BlocksViewLink, BlocksViewNode } from '../types';
import {
  getLCA,
  getOpenAscendant,
  isAscendancyOpen,
  isAscendant,
} from '../viewModel$/util';
import { getColPositions, getRowPositions } from './util';
import { getBlockGrid } from './blockGrid';
import {
  getRowRef,
  routeAscendant,
  routeCousins,
  routeDescendant,
  routeSelf,
  routeSiblings,
} from './route';
import { stripColinearPoints } from '../misc/geometry';
import { last } from 'lodash';
import { RoutingInfo, Waypoint } from './types';

type RoutingChannel = Map<string, number>;

export type Router = {
  routeLink: (link: BlocksViewLink) => Waypoint[];
  adjustSizes: () => void; // boost grid's col and row sizes to make room for routing
  setLinkRoute: () => void; // set route on links
};

/* resolve a waypoint row reference */

const isCorner = (a: Waypoint, b: Waypoint, c: Waypoint) => {
  if (a.colRef !== b.colRef || b.colRef !== c.colRef) {
    return true;
  }

  if (getRowRef(a) !== getRowRef(b) || getRowRef(b) !== getRowRef(c)) {
    return true;
  }

  /* this is a traditional test for colinearity on channels with a notional offset for μchannels */

  const epsilon = 0.01;

  const x = (a: Waypoint) =>
    a.col +
    (a.μcol === -Infinity ? -epsilon : a.μcol === +Infinity ? epsilon : 0);
  const y = (a: Waypoint) =>
    a.row +
    (a.μrow === -Infinity ? -epsilon : a.μrow === +Infinity ? epsilon : 0);

  const u = [x(a) - x(b), y(a) - y(b)];
  const v = [x(c) - x(b), y(c) - y(b)];

  return u[0] * v[1] - u[1] * v[0] !== 0;
};

const stripWaypoints = (waypoints: Waypoint[]) => {
  if (waypoints.length < 3) {
    return waypoints;
  }

  const ret: Waypoint[] = [];

  ret.push(waypoints[0]);

  for (let i = 1; i < waypoints.length - 1; ++i) {
    if (isCorner(last(ret)!, waypoints[i], waypoints[i + 1])) {
      ret.push(waypoints[i]);
    }
  }

  ret.push(last(waypoints)!);

  return ret;
};

export const createRouter = (getBusId: (e: BlocksViewLink) => string) => {
  const routingInfos = new Map<BlocksViewNode, RoutingInfo>();
  const waypoints = new Map<BlocksViewLink, Waypoint[]>();

  const createRoutingInfo = (node: BlocksViewNode) => {
    const colChannels: RoutingChannel[] = [];
    const rowChannels: RoutingChannel[] = [];

    const assignMicroChannel = (
      link: BlocksViewLink,
      channels: RoutingChannel[],
      index: number
    ): number => {
      let channel = channels[index];

      if (!channel) {
        channel = new Map<string, number>();
        channels[index] = channel;
      }

      const busid = getBusId(link);

      let μchannel = channel.get(busid);

      if (μchannel === undefined) {
        μchannel = channel.size;
        channel.set(busid, μchannel);
      }

      return μchannel;
    };

    const assignMicroCol = (link: BlocksViewLink, col: number) => {
      return assignMicroChannel(link, colChannels, col);
    };

    const assignMicroRow = (link: BlocksViewLink, row: number) => {
      return assignMicroChannel(link, rowChannels, row);
    };

    return {
      assignMicroCol: assignMicroCol,
      assignMicroRow: assignMicroRow,
      colChannels: colChannels,
      rowChannels: rowChannels,
      blockGrid: getBlockGrid(node),
    };
  };

  const getRoutingInfo = (node: BlocksViewNode) => {
    let routingInfo: RoutingInfo | undefined = routingInfos.get(node);

    if (!routingInfo) {
      routingInfo = createRoutingInfo(node);

      routingInfos.set(node, routingInfo);
    }

    return routingInfo;
  };

  const calcWaypoints = (link: BlocksViewLink) => {
    link.sourceProxy = undefined;
    link.targetProxy = undefined;
    link.bounds = undefined;

    const lca = getLCA(link.source, link.target);

    if (!lca || !lca.open || !isAscendancyOpen(lca)) {
      /* invisible reference */
      return [];
    }

    link.sourceProxy = getOpenAscendant(link.source);
    link.targetProxy = getOpenAscendant(link.target);

    if (link.sourceProxy === link.targetProxy && link.source !== link.target) {
      /* fake self-reference */
      return [];
    }

    if (link.sourceProxy === link.targetProxy) {
      return routeSelf(link, link.sourceProxy, getRoutingInfo);
    }

    if (link.sourceProxy.parent === lca && link.targetProxy.parent === lca) {
      return routeSiblings(link, getRoutingInfo);
    }

    if (isAscendant(link.targetProxy, link.sourceProxy)) {
      return routeAscendant(link, getRoutingInfo);
    }

    if (isAscendant(link.sourceProxy, link.targetProxy)) {
      return routeDescendant(link, getRoutingInfo);
    }

    return routeCousins(link, getRoutingInfo, lca);
  };

  const assignMicroChannels = (link: BlocksViewLink, waypoints: Waypoint[]) => {
    let colRef = null;
    let colRefInfo = null;
    let rowRef = null;
    let rowRefInfo = null;

    for (let i = 0; i < waypoints.length; ++i) {
      const wp = waypoints[i];

      if (colRef !== wp.colRef) {
        // update cached routinginfo for colRef
        colRef = wp.colRef;
        colRefInfo = getRoutingInfo(colRef);

        // invalidate the μcol cache
      }

      if (isNaN(wp.μcol)) {
        // could cache μcol per col/colRef
        wp.μcol = colRefInfo!.assignMicroCol(link, wp.col);
      }

      if (rowRef !== getRowRef(wp)) {
        // update cached routinginfo for resolved rowRef
        rowRef = getRowRef(wp);
        rowRefInfo = getRoutingInfo(rowRef);

        // invalidate the μrow cache
      }

      if (isNaN(wp.μrow)) {
        // could cache μrow per row/rowRef
        wp.μrow = rowRefInfo!.assignMicroRow(link, wp.row);
      }
    }
  };

  const routeLink = (link: BlocksViewLink) => {
    /* get and strip waypoints */

    const wps = stripWaypoints(calcWaypoints(link));

    /* assign microchannels to the stripped waypoints */

    assignMicroChannels(link, wps);

    /* save final waypoints */

    waypoints.set(link, wps);

    return wps;
  };

  // exported function to boost the node col and row sizes to accomodate the routing
  const adjustSizes = (node: BlocksViewNode) => {
    const routingInfo = routingInfos.get(node);

    if (!routingInfo || !node.colSize || !node.rowSize) {
      return;
    }

    for (let col = 0; col < node.colSize.length; ++col) {
      const channel = routingInfo.colChannels[col];

      if (!channel) {
        continue;
      }

      const μchannels = channel.size;
      const routingWidth =
        μchannels > 0
          ? 2 * BLOCKS_VIEW_ROUTING_MARGIN +
            (μchannels - 1) * BLOCKS_VIEW_ROUTING_SEP
          : 0;

      node.colSize[col] = Math.max(node.colSize[col], routingWidth);
    }

    for (let row = 0; row < node.rowSize.length; ++row) {
      const channel = routingInfo.rowChannels[row];

      if (!channel) {
        continue;
      }

      const μchannels = channel.size;
      const routingHeight =
        μchannels > 0
          ? 2 * BLOCKS_VIEW_ROUTING_MARGIN +
            (μchannels - 1) * BLOCKS_VIEW_ROUTING_SEP
          : 0;

      node.rowSize[row] = Math.max(node.rowSize[row], routingHeight);
    }
  };

  // exported function to create the route from a routed link's waypoints
  const getRoute = (link: BlocksViewLink) => {
    let node: BlocksViewNode | null = null; // cache key
    let routingInfo: RoutingInfo | null = null; // cached routing info for node
    let colPos: number[] = []; // cached column positions for node
    let rowPos: number[] = []; // cached row positions for node

    const linkWaypoints = waypoints.get(link);

    if (!linkWaypoints) {
      return undefined;
    }

    const route: Vector[] = [];

    for (const waypoint of linkWaypoints) {
      /* get column stuff for x */

      if (waypoint.colRef !== node) {
        node = waypoint.colRef;
        routingInfo = routingInfos.get(node!)!;
        colPos = getColPositions(node!);
        rowPos = getRowPositions(node!);
      }

      if (!routingInfo) {
        continue;
      }

      const colWidth = node.colSize![waypoint.col];
      const colChannel = routingInfo.colChannels[waypoint.col];
      const routingWidth = colChannel
        ? (colChannel.size - 1) * BLOCKS_VIEW_ROUTING_SEP
        : 0;
      const x =
        waypoint.μcol === -Infinity
          ? colPos[waypoint.col]
          : waypoint.μcol === +Infinity
            ? colPos[waypoint.col] + colWidth
            : colPos[waypoint.col] +
              0.5 * (colWidth - routingWidth) +
              waypoint.μcol * BLOCKS_VIEW_ROUTING_SEP;

      /* get row stuff and y */

      if (waypoint.rowRef && waypoint.rowRef !== node) {
        node = waypoint.rowRef;
        routingInfo = routingInfos.get(node!)!;
        colPos = getColPositions(node!);
        rowPos = getRowPositions(node!);
      }

      if (!routingInfo) {
        continue;
      }

      const rowHeight = node.rowSize![waypoint.row];
      const rowChannel = routingInfo.rowChannels[waypoint.row];
      const routingHeight = rowChannel
        ? (rowChannel.size - 1) * BLOCKS_VIEW_ROUTING_SEP
        : 0;

      const y =
        waypoint.μrow === -Infinity
          ? rowPos[waypoint.row]
          : waypoint.μrow === +Infinity
            ? rowPos[waypoint.row] + rowHeight
            : rowPos[waypoint.row] +
              0.5 * (rowHeight - routingHeight) +
              waypoint.μrow * BLOCKS_VIEW_ROUTING_SEP;

      route.push([x, y]);
    }

    return stripColinearPoints(
      route,
      v => v[0],
      v => v[1]
    );
  };

  return {
    routeLink: routeLink, // calculate (and remember) the link's waypoints
    adjustSizes: adjustSizes,
    route: getRoute, // calculate the link's positions
  };
};
